import type { SemSummary, RestaurantSemData } from '@/types/sem';
import type { LandingPageReport } from '@/types/landing-page-analysis';

interface PortfolioScoreProps {
  summary: SemSummary;
  restaurants: RestaurantSemData[];
  lpReport: LandingPageReport | null;
}

/**
 * Compute a 0-100 portfolio health score from:
 *  - ROAS performance (30pts)
 *  - Quality Score avg (25pts)
 *  - Conversion rate health (25pts)
 *  - Landing page health (20pts)
 */
function computeScore(
  summary: SemSummary,
  _restaurants: RestaurantSemData[],
  lpReport: LandingPageReport | null
): { score: number; breakdown: { label: string; score: number; max: number; detail: string }[] } {
  const breakdown: { label: string; score: number; max: number; detail: string }[] = [];

  // 1. ROAS (30 pts) — >=8x = full, >=4x = 20, >=2x = 10, <2x = 5
  let roasPts = 5;
  if (summary.roas >= 8) roasPts = 30;
  else if (summary.roas >= 4) roasPts = 20 + Math.round(((summary.roas - 4) / 4) * 10);
  else if (summary.roas >= 2) roasPts = 10 + Math.round(((summary.roas - 2) / 2) * 10);
  breakdown.push({ label: 'ROAS', score: roasPts, max: 30, detail: `${summary.roas.toFixed(1)}x return` });

  // 2. Quality Score (25 pts) — 8+ = full, linear scale
  const qsPts = Math.min(25, Math.round((summary.qualityScoreAvg / 10) * 25));
  breakdown.push({ label: 'Quality Score', score: qsPts, max: 25, detail: `${Math.round(summary.qualityScoreAvg)}/10 avg` });

  // 3. Conversion Rate (25 pts)
  const cvr = summary.clicks > 0 ? (summary.conversions / summary.clicks) * 100 : 0;
  let cvrPts = 5;
  if (cvr >= 8) cvrPts = 25;
  else if (cvr >= 4) cvrPts = 15 + Math.round(((cvr - 4) / 4) * 10);
  else if (cvr >= 2) cvrPts = 8 + Math.round(((cvr - 2) / 2) * 7);
  breakdown.push({ label: 'Conv Rate', score: cvrPts, max: 25, detail: `${cvr.toFixed(1)}% CVR` });

  // 4. Landing Page Health (20 pts)
  let lpPts = 10; // default if no report
  if (lpReport) {
    const avgScore = lpReport.pages.length > 0
      ? lpReport.pages.reduce((s, p) => s + p.scores.overall, 0) / lpReport.pages.length
      : 50;
    lpPts = Math.round((avgScore / 100) * 20);
  }
  breakdown.push({ label: 'LP Health', score: lpPts, max: 20, detail: lpReport ? `${lpReport.pages.length} pages analyzed` : 'Not analyzed' });

  const score = breakdown.reduce((s, b) => s + b.score, 0);
  return { score, breakdown };
}

function getScoreColor(score: number): string {
  if (score >= 75) return 'var(--ed-green)';
  if (score >= 50) return 'var(--ed-amber)';
  return 'var(--ed-red)';
}

function getScoreLabel(score: number): string {
  if (score >= 80) return 'Excellent';
  if (score >= 65) return 'Good';
  if (score >= 50) return 'Needs Work';
  return 'Critical';
}

export function PortfolioScore({ summary, restaurants, lpReport }: PortfolioScoreProps) {
  const { score, breakdown } = computeScore(summary, restaurants, lpReport);
  const color = getScoreColor(score);
  const label = getScoreLabel(score);

  // SVG ring
  const radius = 52;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;

  return (
    <div className="ed-card p-6">
      <div className="flex items-center gap-8">
        {/* Score ring */}
        <div className="relative flex-shrink-0">
          <svg width="128" height="128" viewBox="0 0 128 128">
            {/* Background ring */}
            <circle
              cx="64" cy="64" r={radius}
              fill="none"
              stroke="rgba(255,255,255,0.04)"
              strokeWidth="8"
            />
            {/* Score ring */}
            <circle
              cx="64" cy="64" r={radius}
              fill="none"
              stroke={color}
              strokeWidth="8"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={offset}
              transform="rotate(-90 64 64)"
              style={{ transition: 'stroke-dashoffset 1s ease' }}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-3xl font-display font-light tabular-nums" style={{ color }}>{score}</span>
            <span className="text-[0.55rem] text-muted-foreground/50 uppercase tracking-wider">/100</span>
          </div>
        </div>

        {/* Breakdown */}
        <div className="flex-1">
          <div className="flex items-baseline gap-3 mb-4">
            <h2 className="text-lg font-display font-light tracking-tight">Portfolio Health</h2>
            <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{
              background: `${color}15`,
              color,
              border: `1px solid ${color}20`,
            }}>
              {label}
            </span>
          </div>
          <div className="grid grid-cols-4 gap-3">
            {breakdown.map(b => (
              <div key={b.label}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[0.6rem] text-muted-foreground/50 uppercase tracking-wider">{b.label}</span>
                  <span className="text-[0.6rem] tabular-nums text-muted-foreground/40">{b.score}/{b.max}</span>
                </div>
                <div className="h-1.5 rounded-full bg-white/[0.04] overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${(b.score / b.max) * 100}%`,
                      background: getScoreColor((b.score / b.max) * 100),
                      transition: 'width 1s ease',
                    }}
                  />
                </div>
                <span className="text-[0.55rem] text-muted-foreground/40 mt-1 block">{b.detail}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
